import {
  Call,
  hasScreenShare,
  hasScreenShareAudio,
  StreamVideoParticipant,
  hasAudio,
  hasVideo, 
} from "@stream-io/video-client";

// SVGs for UI elements
const USER_ICON = `<svg viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>`;
const MIC_OFF_ICON = `<svg viewBox="0 0 24 24"><path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l2.97 2.97c-.85.35-1.76.53-2.62.53-3.31 0-6-2.69-6-6v-2.29l-1.67-1.67L3.54 12c0 3.71 2.63 6.84 6.46 7.74V22h4v-2.26c1.19-.28 2.27-.79 3.23-1.46L19.73 21 21 19.73 4.27 3z"/></svg>`;

const videoDimension = { width: 333, height: 250 };
const videoBindingsCache = new Map<string, Function | undefined>();
const audioBindingsCache = new Map<string, Function | undefined>();

const renderVideo = (
  call: Call,
  participant: StreamVideoParticipant,
  parentContainer: HTMLElement
) => {
  const id = `video-${participant.sessionId}`;

  let videoEl = document.getElementById(id) as HTMLVideoElement | null;
  if (!videoEl) {
    videoEl = document.createElement("video");
    videoEl.id = id;
    videoEl.dataset.sessionId = participant.sessionId;
    parentContainer.insertBefore(videoEl, parentContainer.firstChild);

    const unbind = call.bindVideoElement(
      videoEl,
      participant.sessionId,
      "videoTrack"
    );
    videoBindingsCache.set(id, unbind);
  }

  const placeholderId = `placeholder-${participant.sessionId}`;
  let placeholderEl = document.getElementById(placeholderId);

  if (!placeholderEl) {
    placeholderEl = document.createElement("div");
    placeholderEl.id = placeholderId;
    placeholderEl.className = "video-placeholder";
    placeholderEl.innerHTML = USER_ICON;
    parentContainer.appendChild(placeholderEl);
  }

  if (hasVideo(participant)) {
    videoEl.style.display = "block";
    placeholderEl.style.display = "none";
  } else {
    videoEl.style.display = "none";
    placeholderEl.style.display = "flex";
  }
};

const renderAudio = (
  call: Call,
  participant: StreamVideoParticipant,
  parentContainer: HTMLElement
) => {
  if (participant.isLocalParticipant) return;
  const id = `audio-${participant.sessionId}`;
  let audioEl = document.getElementById(id) as HTMLAudioElement | null;
  if (!audioEl) {
    audioEl = document.createElement("audio");
    audioEl.id = id;
    audioEl.dataset.sessionId = participant.sessionId;
    parentContainer.appendChild(audioEl);
    const unbind = call.bindAudioElement(audioEl, participant.sessionId);
    audioBindingsCache.set(id, unbind);
  }
};

const renderStatusIcons = (
  participant: StreamVideoParticipant,
  parentContainer: HTMLElement
) => {
  const micId = `mic-status-${participant.sessionId}`;
  let micEl = document.getElementById(micId);

  if (!micEl) {
    micEl = document.createElement("div");
    micEl.id = micId;
    micEl.className = "mic-status";
    micEl.innerHTML = MIC_OFF_ICON;
    parentContainer.appendChild(micEl);
  }

  micEl.style.display = hasAudio(participant) ? "none" : "flex";
};

export const renderParticipant = (
  call: Call,
  participant: StreamVideoParticipant,
  parentContainer: HTMLElement,
  screenShareContainer?: HTMLElement
) => {
  renderVideo(call, participant, parentContainer);
  renderAudio(call, participant, parentContainer);
  renderStatusIcons(participant, parentContainer);

  if (hasScreenShare(participant) && screenShareContainer) {
     // (Screen share logic omitted)
  }
};

export const cleanupParticipant = (sessionId: string) => {
  const unbindVideo = videoBindingsCache.get(`video-${sessionId}`);
  if (unbindVideo) unbindVideo();
  videoBindingsCache.delete(`video-${sessionId}`);

  const unbindAudio = audioBindingsCache.get(`audio-${sessionId}`);
  if (unbindAudio) unbindAudio();
  audioBindingsCache.delete(`audio-${sessionId}`);
};